/**
 * BarcodeScannerModal — Scan code-barres via caméra (iPad/desktop)
 *
 * Utilise @zxing/browser pour décoder en continu depuis le flux caméra.
 * Compatible Safari/Chrome iOS (WebKit) car BarcodeDetector natif n'y est pas dispo.
 *
 * Modes :
 *  - single (défaut) : ferme après le premier scan
 *  - continuous : reste ouvert, debounce le même code pendant 1.5s
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import {
  DecodeHintType,
  BarcodeFormat,
  type Result,
  type Exception,
} from '@zxing/library';

interface BarcodeScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onScan: (code: string) => void;
  continuous?: boolean;
  title?: string;
}

const SCAN_FORMATS = [
  BarcodeFormat.EAN_13,
  BarcodeFormat.EAN_8,
  BarcodeFormat.UPC_A,
  BarcodeFormat.UPC_E,
  BarcodeFormat.CODE_128,
  BarcodeFormat.CODE_39,
  BarcodeFormat.ITF,
  BarcodeFormat.QR_CODE,
];

function beep() {
  try {
    const AudioCtx =
      (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = 880;
    osc.type = 'square';
    gain.gain.value = 0.15;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.08);
    setTimeout(() => ctx.close(), 200);
  } catch {
    // pas de feedback audio si bloqué
  }
}

export default function BarcodeScannerModal({
  isOpen,
  onClose,
  onScan,
  continuous = false,
  title = 'Scanner un code-barres',
}: BarcodeScannerModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const readerRef = useRef<BrowserMultiFormatReader | null>(null);
  const controlsRef = useRef<{ stop: () => void } | null>(null);
  const wakeLockRef = useRef<{ release: () => Promise<void> } | null>(null);
  const lastScanRef = useRef<{ code: string; at: number }>({ code: '', at: 0 });

  const [error, setError] = useState<string | null>(null);
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<string>('');
  const [lastDetected, setLastDetected] = useState<string | null>(null);
  const [flash, setFlash] = useState(false);

  const stopScanner = useCallback(() => {
    try {
      controlsRef.current?.stop();
    } catch {
      // ignore
    }
    controlsRef.current = null;

    const stream = videoRef.current?.srcObject as MediaStream | null;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      if (videoRef.current) videoRef.current.srcObject = null;
    }

    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
    }
  }, []);

  const handleDecode = useCallback(
    (result: Result | undefined, _err: Exception | undefined) => {
      if (!result) return;
      const code = result.getText().trim();
      if (!code) return;

      // Anti-rebond : ignorer si même code détecté il y a moins de 1.5s
      const now = Date.now();
      if (
        lastScanRef.current.code === code &&
        now - lastScanRef.current.at < 1500
      ) {
        return;
      }
      lastScanRef.current = { code, at: now };

      beep();
      setLastDetected(code);
      setFlash(true);
      setTimeout(() => setFlash(false), 200);

      onScan(code);

      if (!continuous) {
        stopScanner();
        onClose();
      }
    },
    [onScan, continuous, onClose, stopScanner],
  );

  const startScanner = useCallback(
    async (deviceId?: string) => {
      setError(null);

      // Configuration ZXing : limiter aux formats utiles + try harder
      const hints = new Map();
      hints.set(DecodeHintType.POSSIBLE_FORMATS, SCAN_FORMATS);
      hints.set(DecodeHintType.TRY_HARDER, true);

      const reader = new BrowserMultiFormatReader(hints, {
        delayBetweenScanAttempts: 100,
      });
      readerRef.current = reader;

      try {
        // Vérifier support
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error('Caméra non supportée par ce navigateur.');
        }

        // Démarrer le décodage depuis la caméra
        const constraints: MediaStreamConstraints = {
          video: deviceId
            ? { deviceId: { exact: deviceId } }
            : { facingMode: { ideal: 'environment' } },
          audio: false,
        };

        const controls = await reader.decodeFromConstraints(
          constraints,
          videoRef.current!,
          handleDecode,
        );
        controlsRef.current = controls;

        // Lister les caméras dispos (après permission)
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const videoDevices = devices.filter((d) => d.kind === 'videoinput');
          setCameras(videoDevices);
          if (!deviceId) {
            const stream = videoRef.current?.srcObject as MediaStream | null;
            const activeTrack = stream?.getVideoTracks()[0];
            const activeId = activeTrack?.getSettings().deviceId;
            if (activeId) setSelectedCamera(activeId);
          }
        } catch {
          // pas grave
        }

        // Wake lock pour garder l'écran allumé (iOS 16.4+)
        try {
          const nav = navigator as unknown as {
            wakeLock?: { request: (type: 'screen') => Promise<{ release: () => Promise<void> }> };
          };
          if (nav.wakeLock) {
            wakeLockRef.current = await nav.wakeLock.request('screen');
          }
        } catch {
          // pas critique
        }
      } catch (err) {
        const e = err as { name?: string; message?: string };
        if (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError') {
          setError(
            "Accès caméra refusé. Autorisez l'accès dans Réglages → Safari/Chrome → Caméra.",
          );
        } else if (e.name === 'NotFoundError' || e.name === 'DevicesNotFoundError') {
          setError('Aucune caméra trouvée sur cet appareil.');
        } else if (e.name === 'NotReadableError') {
          setError('Caméra déjà utilisée par une autre application.');
        } else {
          setError(`Erreur caméra : ${e.message ?? 'inconnue'}`);
        }
      }
    },
    [handleDecode],
  );

  useEffect(() => {
    if (isOpen) {
      setLastDetected(null);
      lastScanRef.current = { code: '', at: 0 };
      startScanner();
    } else {
      stopScanner();
    }
    return () => stopScanner();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  const handleCameraChange = (deviceId: string) => {
    setSelectedCamera(deviceId);
    stopScanner();
    startScanner(deviceId);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-black flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/80 text-white">
        <div className="flex items-center gap-3 min-w-0">
          <h3 className="font-semibold truncate">{title}</h3>
          {cameras.length > 1 && (
            <select
              value={selectedCamera}
              onChange={(e) => handleCameraChange(e.target.value)}
              className="text-xs bg-white/10 border border-white/20 rounded px-2 py-1 text-white"
            >
              {cameras.map((cam, i) => (
                <option key={cam.deviceId} value={cam.deviceId} className="text-black">
                  {cam.label || `Caméra ${i + 1}`}
                </option>
              ))}
            </select>
          )}
        </div>
        <button
          onClick={onClose}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20"
          aria-label="Fermer"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Zone vidéo */}
      <div className="relative flex-1 overflow-hidden bg-black">
        {error ? (
          <div className="absolute inset-0 flex items-center justify-center p-8 text-center text-white">
            <div className="max-w-md">
              <svg className="w-16 h-16 mx-auto mb-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
              <p className="text-base">{error}</p>
              <button
                onClick={onClose}
                className="mt-6 px-5 py-2 bg-white/10 hover:bg-white/20 rounded-lg"
              >
                Fermer
              </button>
            </div>
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
            {/* Viseur */}
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="relative w-[80%] max-w-md aspect-[3/2]">
                <div className="absolute inset-0 border-2 border-emerald-400/80 rounded-lg" />
                {/* Coins */}
                {(['top-0 left-0', 'top-0 right-0', 'bottom-0 left-0', 'bottom-0 right-0'] as const).map((pos) => (
                  <div
                    key={pos}
                    className={`absolute ${pos} w-8 h-8 border-emerald-400 ${
                      pos.includes('top') ? 'border-t-4' : 'border-b-4'
                    } ${pos.includes('left') ? 'border-l-4' : 'border-r-4'} rounded-sm`}
                  />
                ))}
                {/* Ligne laser */}
                <div className="absolute inset-x-4 top-1/2 h-0.5 bg-red-500 shadow-[0_0_8px_2px_rgba(239,68,68,0.7)]" />
              </div>
            </div>

            {/* Flash de confirmation */}
            {flash && (
              <div className="absolute inset-0 bg-emerald-400/30 pointer-events-none transition-opacity" />
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 bg-black/80 text-white">
        {lastDetected ? (
          <div className="text-center">
            <div className="text-xs text-emerald-400">Dernier code détecté</div>
            <div className="font-mono text-lg">{lastDetected}</div>
            {continuous && (
              <div className="text-xs text-white/60 mt-1">
                Continuez à scanner les codes-barres
              </div>
            )}
          </div>
        ) : (
          <div className="text-center text-sm text-white/70">
            Placez le code-barres dans le cadre vert
          </div>
        )}
      </div>
    </div>
  );
}
